import { Formik } from "formik";
import React, { useMemo, useState } from "react";
import theme from "../../../theme";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2, H3 } from "../../../components/Heading";
import { Label } from "../../../components/Label";
import { Paragraph } from "../../../components/Paragraph";
import Select from "../../../components/Select";
import { useAddAddress } from "../../../data/useAddAddress";
import { useCountries } from "../../../data/useCountries";
import { showError } from "../../../utils/error-handling";
import useAddressLongForm from "../../../utils/forms/useAddressLongForm";
import { addressFormValidationSchema } from "../../../utils/validationSchema";
import { ROUTE } from "../../../routes";
import { NewProfileScreeningFrame } from "./NewProfileScreeningFrame";
import { CheckAddress } from "../../../types/CheckAddress";

type Option = {
  label: string;
  value: string | number;
  sac: boolean;
};

const renderLine1 = (a: any) =>
  [
    a?.flat_appartment_subbuilding,
    a?.building_number,
    a?.building_name,
    a?.road_street,
  ]
    .filter(Boolean)
    .join(" ");

const isBlank = (v: any) =>
  v == null || (typeof v === "string" && v.trim().length === 0);

const Field = ({
  label,
  value,
  right,
}: {
  label: string;
  value: React.ReactNode;
  right?: boolean;
}) => (
  <Box flex={1} pl={right ? 4 : 0}>
    <Paragraph fontSize={1} color="gray.60" mb={1}>
      {label}
    </Paragraph>

    <Paragraph mb={0} style={{ fontWeight: 600 }}>
      {isBlank(value) ? "-" : value}
    </Paragraph>
  </Box>
);

const CountryValue = ({ a }: { a: any }) => (
  <Flex alignItems="center" gap="8">
    {a?.country_flag && (
      <Box backgroundColor="gray.10" borderRadius="50%" p="1">
        <img
          src={`data:image/png;base64,${a.country_flag}`}
          alt="flag"
          style={{
            display: "block",
            width: 16,
            height: 16,
            borderRadius: "50%",
          }}
        />
      </Box>
    )}
    <span>{a?.country_name || a?.country || "-"}</span>
  </Flex>
);

export const AddScreeningAddressStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();
  const state: any = location.state ?? {};

  const { data: countries } = useCountries();
  const { mutate: addAddress, isLoading } = useAddAddress();
  const { AddressLongForm } = useAddressLongForm();

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [showNoCoverageBox, setShowNoCoverageBox] = useState<boolean | null>(
    null,
  );
  const [formOpen, setFormOpen] = useState(false);
  const [doubleClick, setDoubleClick] = useState(false);

  const targetId = Number(state?.createdRecordId);
  const hasTargetId = Boolean(targetId) && !Number.isNaN(targetId);

  const registeredAddress = useMemo(() => {
    const addresses = profile?.address ?? [];

    return (
      addresses.find((a: any) => a?._type?.name === "Primary Residence") ||
      addresses.find((a: any) => a?._type?.name === "Residential") ||
      addresses[0] ||
      null
    );
  }, [profile?.address]);

  const hasRegisteredAddress =
    !!registeredAddress &&
    !!(
      registeredAddress?.road_street ||
      registeredAddress?.building_number ||
      registeredAddress?.post_zip_code ||
      registeredAddress?.address_line_1
    );

  const countryOptions: Option[] = (countries?.data ?? []).map(
    ({ isoalpha2, name, sac }: any) => ({
      label: name,
      value: isoalpha2,
      sac: Boolean(sac),
    }),
  );

  useMemo(() => {
    if (!hasRegisteredAddress || selectedCountry) return;

    const countryCode =
      registeredAddress?.country_code ||
      registeredAddress?.country_isoalpha2 ||
      registeredAddress?.country_iso ||
      registeredAddress?.country;

    if (!countryCode) return;

    const opt = countryOptions.find(
      (c) =>
        String(c.value).toLowerCase() === String(countryCode).toLowerCase(),
    );
    if (opt) setSelectedCountry(opt);
  }, [
    hasRegisteredAddress,
    registeredAddress,
    countryOptions,
    selectedCountry,
  ]);

  const initialValues: CheckAddress = {
    building_number: "",
    flat_appartment_subbuilding: "",
    building_name: "",
    road_street: "",
    town_city: "",
    district: "",
    state_province_name: "",
    state_province_code: "",
    post_zip_code: "",
    country_code: "",
  };

  const goBack = () => {
    navigate(
      ROUTE.PROFILE_SCREENING_ADD_PERSON.replace(":id", profileId.toString()),
      { state },
    );
  };

  const goReview = (extra?: any) => {
    navigate(
      ROUTE.PROFILE_SCREENING_REVIEW.replace(":id", profileId.toString()),
      { state: { ...state, ...extra } },
    );
  };

  const submitAddress = (payload: CheckAddress) => {
    if (!hasTargetId) {
      showError(null as any, "Missing createdRecordId for individual.");
      return;
    }
    if (doubleClick) return;

    setDoubleClick(true);

    addAddress(
      { id: targetId, address: payload },
      {
        onSuccess: (res: any) => {
          setDoubleClick(false);
          goReview({
            confirmedAddress: res?.data?.address ?? payload,
            createdRecordId: targetId,
          });
        },
        onError: (err: any) => {
          setDoubleClick(false);
          showError(err, "A problem occurred while saving the address.");
        },
      },
    );
  };

  const handleNextWithExisting = () => {
    const payload: CheckAddress = {
      flat_appartment_subbuilding:
        registeredAddress?.flat_appartment_subbuilding ?? "",
      building_number: registeredAddress?.building_number ?? "",
      building_name: registeredAddress?.building_name ?? "",
      road_street: registeredAddress?.road_street ?? "",
      town_city: registeredAddress?.town_city ?? "",
      district: registeredAddress?.district ?? "",
      state_province_name: registeredAddress?.state_province_name ?? "",
      state_province_code: registeredAddress?.state_province_code ?? "",
      post_zip_code: registeredAddress?.post_zip_code ?? "",
      country_code:
        String(
          selectedCountry?.value ??
            registeredAddress?.country_code ??
            registeredAddress?.country_isoalpha2 ??
            registeredAddress?.country ??
            "",
        ) || "",
    };

    submitAddress(payload);
  };

  const handleNextWithForm = (values: CheckAddress) => {
    if (!selectedCountry) return;

    const payload: CheckAddress = {
      ...values,
      country_code: String(selectedCountry.value),
    };

    submitAddress(payload);
  };

  const addressLabel = useMemo(() => {
    if (!registeredAddress) return "-";
    return [renderLine1(registeredAddress), registeredAddress?.post_zip_code]
      .filter(Boolean)
      .join(", ");
  }, [registeredAddress]);

  return (
    <NewProfileScreeningFrame step="addAddress" mode="person">
      <Box mt={2} maxWidth={720} minWidth={650}>
        <H2>Residential Address</H2>

        {hasRegisteredAddress ? (
          <>
            <Box
              mt={3}
              style={{
                borderTop: `1px solid ${theme.colors.divider}`,
                borderBottom: `1px solid ${theme.colors.divider}`,
                paddingTop: 16,
                paddingBottom: 16,
              }}
            >
              <Flex>
                <Field
                  label="Country"
                  value={<CountryValue a={registeredAddress} />}
                />
                <Field label="Address" value={addressLabel || "-"} right />
              </Flex>

              <Flex mt={3}>
                <Field
                  label="Flat Number"
                  value={registeredAddress?.flat_appartment_subbuilding}
                />
                <Field
                  label="Building Number"
                  value={registeredAddress?.building_number}
                  right
                />
              </Flex>

              <Flex mt={3}>
                <Field
                  label="Building Name"
                  value={registeredAddress?.building_name}
                />
                <Field
                  label="Street"
                  value={registeredAddress?.road_street}
                  right
                />
              </Flex>

              <Flex mt={3}>
                <Field label="Town/City" value={registeredAddress?.town_city} />
                <Field
                  label="County / district"
                  value={registeredAddress?.district}
                  right
                />
              </Flex>

              <Flex mt={3}>
                <Field
                  label="Province name"
                  value={registeredAddress?.state_province_name}
                />
                <Field
                  label="Province code"
                  value={registeredAddress?.state_province_code}
                  right
                />
              </Flex>

              <Flex mt={3}>
                <Field
                  label="Post code / ZIP code"
                  value={registeredAddress?.post_zip_code}
                />
                <Box flex={1} />
              </Flex>
            </Box>

            <Flex justifyContent="space-between" mt={3}>
              <Button variant="text" onClick={goBack}>
                Cancel
              </Button>
              <Button
                onClick={handleNextWithExisting}
                isDisabled={isLoading || doubleClick || !hasTargetId}
              >
                Next
              </Button>
            </Flex>
          </>
        ) : (
          <Box mt={3}>
            <Box position="relative">
              <Select
                value={selectedCountry}
                options={countryOptions}
                onChange={(option: Option) => {
                  setSelectedCountry(option);
                  setShowNoCoverageBox(!option.sac);
                }}
                minWidth={200}
                width={600}
                flex={1}
                label="Country"
                name="country"
                isRequired
                hasError={Boolean(!selectedCountry)}
                errorMessage={selectedCountry ? "" : "Country is required"}
              />

              {showNoCoverageBox && (
                <Box
                  width={250}
                  position="fixed"
                  top={250}
                  right={30}
                  backgroundColor="gray.100"
                  p={1}
                  borderRadius={12}
                >
                  <H3 fontSize={1}>No address coverage</H3>
                  <Paragraph fontSize={0}>
                    Our address verification service does not currently cover
                    the country you have selected.
                  </Paragraph>
                </Box>
              )}
            </Box>

            {selectedCountry && (
              <Box width={600}>
                <Formik
                  initialValues={initialValues}
                  validationSchema={() =>
                    addressFormValidationSchema(
                      selectedCountry.value.toString(),
                    )
                  }
                  onSubmit={handleNextWithForm}
                  validateOnMount
                >
                  {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                      <Box mt={2}>
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Label>Address</Label>
                          <Button
                            variant="text"
                            type="button"
                            onClick={() => setFormOpen(true)}
                          >
                            Enter address manually
                          </Button>
                        </Flex>

                        <AddressAutocomplete
                          setSelectedAddress={(address: any) => {
                            setSelectedAddress(address);
                            setFormOpen(true);
                            Object.keys(address).forEach((key) => {
                              formik.setFieldValue(key, (address as any)[key]);
                            });
                          }}
                          country={selectedCountry.value}
                          setFormOpen={setFormOpen}
                        />
                      </Box>

                      {formOpen && <AddressLongForm formik={formik} />}

                      <Flex justifyContent="space-between" my={3}>
                        <Button variant="text" type="button" onClick={goBack}>
                          Cancel
                        </Button>

                        <Button
                          type="submit"
                          isDisabled={
                            !hasTargetId ||
                            doubleClick ||
                            isLoading ||
                            (!selectedAddress &&
                              !(formik.isValid && formik.dirty))
                          }
                        >
                          Next
                        </Button>
                      </Flex>
                    </form>
                  )}
                </Formik>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </NewProfileScreeningFrame>
  );
};
